import pickBy from 'lodash/pickBy';

import { createResourceLocatorString, matchPathname } from './routes';
import { parse, stringify } from './urlHelpers';

const STRIP_FROM_RETURN = ['access', 'accessContext', 'from', 'mobilemenu', 'mobilesearch'];

const LISTING_ROUTE_NAMES = new Set(['ListingPage', 'ListingPageVariant', 'ListingPageCanonical']);

/**
 * Construye un objeto tipo `location` a partir de `from` guardado en history state
 * (pathname + search + hash relativos a la app).
 */
export function locationFromAuthReturnFrom(from, fallbackLocation) {
  if (!from || typeof from !== 'string') {
    return fallbackLocation;
  }
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const u = new URL(from, origin);
    return {
      ...fallbackLocation,
      pathname: u.pathname,
      search: u.search,
      hash: u.hash,
    };
  } catch (e) {
    return fallbackLocation;
  }
}

/**
 * URL de retorno tras auth sin parámetros solo del modal (access, etc.).
 */
export function returnPathAfterAuthFromLocation(location) {
  const params = parse(location.search || '');
  const cleaned = pickBy(params, (v, k) => !STRIP_FROM_RETURN.includes(k));
  const q = stringify(cleaned);
  return `${location.pathname}${q ? `?${q}` : ''}${location.hash || ''}`;
}

/**
 * True when post-auth return URL is "publicar anuncio" (/l/new), e.g. registro desde nueva ficha.
 */
export function isNewListingHostSignupReturnPath(from) {
  if (!from || typeof from !== 'string') {
    return false;
  }
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const u = new URL(from, origin);
    const pathnameNorm = (u.pathname || '').replace(/\/$/, '') || '/';
    return pathnameNorm === '/l/new';
  } catch (e) {
    return false;
  }
}

/**
 * Safe app-relative post-auth return path from history state or `?from=`.
 * Returns null when absent or not a same-origin relative path (open-redirect guard).
 */
export function authReturnPathFromLocation(location) {
  const fromState = location?.state?.from;
  if (typeof fromState === 'string' && fromState.startsWith('/') && !fromState.startsWith('//')) {
    return fromState;
  }
  const fromParam = parse(location?.search || '').from;
  if (typeof fromParam === 'string' && fromParam.startsWith('/') && !fromParam.startsWith('//')) {
    return fromParam;
  }
  return null;
}

/**
 * Tras login/registro desde el modal de acceso: checkout del listing si estamos en ficha;
 * si no, misma ruta limpiando query del modal.
 */
export function postSuccessPathAfterAccessModal(location, routeConfiguration) {
  // A `from` deep-link carried into the modal (e.g. via the /login redirect or an
  // auth-gated page) takes precedence: return the user where they were headed.
  const fromDeepLink = authReturnPathFromLocation(location);
  if (fromDeepLink) {
    return fromDeepLink;
  }

  const { pathname } = location;
  const matches = matchPathname(pathname, routeConfiguration);
  const listingMatch = matches.find(m => m.route?.name && LISTING_ROUTE_NAMES.has(m.route.name));
  const params = listingMatch?.params || {};
  const { slug, id } = params;
  if (id) {
    return createResourceLocatorString('CheckoutPage', routeConfiguration, {
      slug: slug || 'no-slug',
      id,
    });
  }
  return returnPathAfterAuthFromLocation(location);
}

/**
 * Abre el modal de acceso del topbar (query access=open) manteniendo la ruta actual.
 *
 * @param {object} history - react-router history
 * @param {object} location - react-router location
 * @param {object} [options]
 * @param {object|null} [options.stateMerge] - se fusiona con location.state (p. ej. { from: '...' })
 * @param {string|null} [options.accessContext] - p. ej. 'booking' para signup desde reserva/mensaje en listing
 */
export function openAccessLoginModal(history, location, options = {}) {
  const { stateMerge = null, accessContext = null } = options;
  const { pathname, search, hash, state } = location;
  const params = parse(search || '');
  const merged = { ...params, access: 'open' };
  if (accessContext) {
    merged.accessContext = accessContext;
  }
  const nextSearch = stringify(merged);
  const qs = nextSearch ? `?${nextSearch}` : '?access=open';
  const nextState = stateMerge != null ? { ...(state || {}), ...stateMerge } : state;
  history.push(`${pathname}${qs}${hash || ''}`, nextState);
}
